import { Box } from "@mui/material";
import { LocalDrinkOutlined as WaterIcon } from "@mui/icons-material";
import { Card } from "../ui/primitives";
import { v2, nums } from "../ui/v2Tokens";

/** Trainers say "three litres a day"; the model stores millilitres. */
const ML_PER_LITRE = 1000;

/** The same cup the trainee's app counts in, so "12 cups" here means 12 taps there. */
const CUP_ML = 250;

/** The answers that cover almost every menu, one click each. */
const PRESETS_L = [2, 2.5, 3, 3.5, 4];

const round1 = (n: number) => Math.round(n * 10) / 10;

type Props = {
  /** millilitres, or undefined when the menu sets no goal */
  waterGoalMl?: number;
  onChange: (waterGoalMl: number | undefined) => void;
};

/**
 * The menu's daily hydration goal.
 *
 * Sits beside supplements rather than in the macro grid, and for the same reason supplements
 * do: water carries no energy, so it belongs to no part of the calorie budget. Putting it in
 * that grid would have made one of six cells mean something different from the other five.
 *
 * Entered in LITRES because that is how the instruction is given, stored in millilitres
 * because that is what the trainee's app logs in — a 250ml cup has to divide the goal
 * evenly, and it cannot if the goal is a rounded number of litres held as a float.
 *
 * Optional on purpose. No goal is a real answer, and the trainee's water card hides itself
 * rather than showing a target of zero — so "off" has to be reachable from here.
 */
const WaterGoalSection = ({ waterGoalMl, onChange }: Props) => {
  const litres = waterGoalMl ? round1(waterGoalMl / ML_PER_LITRE) : 0;
  const cups = waterGoalMl ? Math.round(waterGoalMl / CUP_ML) : 0;

  const setLitres = (text: string) => {
    const parsed = parseFloat(text);
    // an empty or nonsensical field means "no goal", not "a goal of zero"
    if (Number.isNaN(parsed) || parsed <= 0) return onChange(undefined);
    onChange(Math.round(parsed * ML_PER_LITRE));
  };

  return (
    <Card data-testid="water-goal-section">
      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          padding: "14px 16px",
          borderBottom: `1px solid ${v2.lineSoft}`,
        }}
      >
        <WaterIcon sx={{ fontSize: 18, color: v2.ink2 }} />
        <Box sx={{ fontSize: "15px", fontWeight: 600, color: v2.ink }}>יעד שתייה</Box>
        <Box sx={{ fontSize: "12.5px", color: v2.ink3 }}>
          {waterGoalMl ? `${litres} ליטר ביום` : "לא הוגדר"}
        </Box>
        <Box sx={{ marginInlineStart: "auto", fontSize: "12px", color: v2.ink4 }}>
          אינו נספר ביעדים היומיים
        </Box>
      </Box>

      <Box
        sx={{
          display: "flex",
          alignItems: "center",
          gap: "10px",
          flexWrap: "wrap",
          padding: "14px 16px",
        }}
      >
        <Box sx={{ position: "relative", display: "flex", alignItems: "center" }}>
          <Box
            component="input"
            type="number"
            aria-label="יעד שתייה בליטרים"
            min={0}
            max={10}
            step={0.25}
            placeholder="0"
            value={waterGoalMl ? litres : ""}
            onChange={(e: React.ChangeEvent<HTMLInputElement>) => setLitres(e.target.value)}
            sx={{
              boxSizing: "border-box",
              width: "120px",
              height: "38px",
              padding: "0 46px 0 11px",
              borderRadius: v2.rSm,
              border: `1px solid ${v2.line}`,
              background: v2.surface,
              fontSize: "14px",
              fontFamily: "inherit",
              color: v2.ink,
              outline: "none",
              ...nums,
              "&::placeholder": { color: v2.ink4 },
              "&:focus": { borderColor: v2.brand, boxShadow: `0 0 0 3px ${v2.brandWash}` },
            }}
          />
          <Box
            sx={{ position: "absolute", insetInlineEnd: "11px", fontSize: "12px", color: v2.ink4, pointerEvents: "none" }}
          >
            ליטר
          </Box>
        </Box>

        <Box sx={{ display: "flex", gap: "6px", flexWrap: "wrap" }}>
          {PRESETS_L.map((preset) => {
            const on = waterGoalMl === preset * ML_PER_LITRE;
            return (
              <Box
                key={preset}
                component="button"
                type="button"
                aria-label={`יעד ${preset} ליטר`}
                aria-pressed={on}
                onClick={() => onChange(on ? undefined : preset * ML_PER_LITRE)}
                sx={{
                  height: "32px",
                  padding: "0 12px",
                  borderRadius: v2.rSm,
                  border: `1px solid ${on ? v2.brand : v2.line}`,
                  background: on ? v2.brandWash : v2.surface,
                  color: on ? v2.brandText : v2.ink2,
                  fontSize: "13px",
                  fontWeight: on ? 600 : 500,
                  fontFamily: "inherit",
                  cursor: "pointer",
                  transition: "all .12s",
                  ...nums,
                  "&:hover": { borderColor: on ? v2.brand : v2.ink4 },
                }}
              >
                {preset}
              </Box>
            );
          })}
        </Box>

        {waterGoalMl ? (
          <Box sx={{ fontSize: "12.5px", color: v2.ink3, marginInlineStart: "auto", ...nums }}>
            {`${waterGoalMl.toLocaleString("he-IL")} מ״ל · כ-${cups} כוסות של ${CUP_ML} מ״ל`}
          </Box>
        ) : (
          <Box sx={{ fontSize: "12.5px", color: v2.ink4, marginInlineStart: "auto" }}>
            בלי יעד, המתאמן לא יראה מעקב שתייה
          </Box>
        )}
      </Box>
    </Card>
  );
};

export default WaterGoalSection;
